import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { RestfulApiService } from '../restful-api.service';

export type BadgeSortBy = 'ALPHABETICAL' | 'RANK';
export type BadgeOrder = 'ASC' | 'DESC';

export interface BadgeCatalogRequest {
  order: BadgeOrder;
  searchText: string;
  sortBy: BadgeSortBy;
}

export interface BadgeCatalogItem {
  uuid: string;
  name: string;
  rank: number;
  imageUrl: string;
  numberOfChampions: number;
}

export interface BadgeCatalogResponse {
  response: BadgeCatalogItem[];
}

export interface BadgeChampion {
  uuid: string;
  name: string;
  imageUrl: string | null;
}

export interface BadgeChampionsResponse {
  response: BadgeChampion[];
}

export interface BadgeUploadImageResponse {
  response: {
    uuid: string;
    url: string;
    type: string;
  };
}

export interface BadgeCreateUpdatePayload {
  uuid: string | null;
  name: string;
  rank: number;
  imageUuid: string | null;
  championSetUuids: string[];
}

export interface BadgeCreateUpdateResponse {
  response: BadgeCatalogItem;
}

export interface BadgeCheckNameResponse {
  /** true = name already taken; false = name is available */
  response: boolean;
}

export interface BadgeDeleteResponse {
  response: string;
}

export interface BadgeDetailChampion {
  uuid: string;
  name: string;
}

export interface BadgeDetailImage {
  uuid: string;
  url: string;
  filename: string;
  type: string;
  fileSize?: number;
}

export interface BadgeDetail {
  uuid: string;
  name: string;
  rank: number;
  champions: BadgeDetailChampion[];
  image: BadgeDetailImage | null;
}

export interface BadgeDetailResponse {
  response: BadgeDetail;
}

export interface BadgeImageDeleteResponse {
  response: string;
}

@Injectable({
  providedIn: 'root'
})
export class BadgeService {
  private readonly catalogEndpoint = '/v1/badge/admin/catalog';
  private readonly championsEndpoint = '/v1/badge/champions';
  private readonly createUpdateEndpoint = '/v1/badge/create-update';
  private readonly checkNameEndpoint = '/v1/badge/check-name';
  private readonly deleteEndpoint = '/v1/badge/delete';
  private readonly uploadEndpoint = '/v1/general/upload/file';

  constructor(private apiService: RestfulApiService) {}

  /**
   * Fetch the badge admin catalog.
   */
  getBadgeCatalog(
    payload: BadgeCatalogRequest = { order: 'ASC', searchText: '', sortBy: 'ALPHABETICAL' }
  ): Observable<BadgeCatalogResponse> {
    return this.apiService.post(this.catalogEndpoint, payload) as Observable<BadgeCatalogResponse>;
  }

  /**
   * Fetch the list of champions available to assign to a badge.
   */
  getChampions(): Observable<BadgeChampionsResponse> {
    return this.apiService.get(this.championsEndpoint) as Observable<BadgeChampionsResponse>;
  }

  /**
   * Upload a badge image.
   * Sends multipart/form-data matching the /v1/general/upload/file contract.
   */
  uploadBadgeImage(file: File, entityUuid?: string): Observable<BadgeUploadImageResponse> {
    const formData = new FormData();
    formData.append('entityUuid', entityUuid ?? '');
    formData.append('fileType', 'BADGE_IMAGE');
    formData.append('secondUuid', '');
    formData.append('file', file);
    return this.apiService.post(this.uploadEndpoint, formData) as Observable<BadgeUploadImageResponse>;
  }

  /**
   * Check whether a badge name is already taken.
   * Pass uuid when updating an existing badge so the check excludes itself.
   * Response: true = name taken; false = name available.
   */
  checkBadgeName(name: string, uuid?: string): Observable<BadgeCheckNameResponse> {
    const payload = uuid ? { name, uuid } : { name };
    return this.apiService.post(this.checkNameEndpoint, payload) as Observable<BadgeCheckNameResponse>;
  }

  /**
   * Create or update a badge.
   * Set uuid to null for creation.
   */
  createUpdateBadge(payload: BadgeCreateUpdatePayload): Observable<BadgeCreateUpdateResponse> {
    return this.apiService.post(this.createUpdateEndpoint, payload) as Observable<BadgeCreateUpdateResponse>;
  }

  /**
   * Delete a badge by UUID.
   */
  deleteBadge(badgeUuid: string): Observable<BadgeDeleteResponse> {
    return this.apiService.delete(`${this.deleteEndpoint}/${badgeUuid}`) as Observable<BadgeDeleteResponse>;
  }

  /**
   * Fetch full badge details including champions and image metadata.
   */
  getBadgeDetail(uuid: string): Observable<BadgeDetailResponse> {
    return this.apiService.get(`/v1/badge/${uuid}`) as Observable<BadgeDetailResponse>;
  }

  /**
   * Delete a badge image file.
   * entityUuid is intentionally empty per the API contract.
   */
  deleteBadgeImage(imageUuid: string): Observable<BadgeImageDeleteResponse> {
    return this.apiService.delete('/v1/general/delete/file', {
      entityUuid: '',
      fileType: 'BADGE_IMAGE',
      fileUuid: imageUuid,
    }) as Observable<BadgeImageDeleteResponse>;
  }
}
